import type { AppSpecFull } from '@/services/block-api-service'
// The intake-decisions subpath, NOT the package root: the root barrel pulls
// the generation plugins → @cogo/validator root → node:fs, which kills the
// browser boot (measured 2026-08-08 — the appliance studio home rendered
// blank with "Module node:fs has been externalized" the first time this
// module was loaded on a cold vite graph; long-lived HMR had masked it).
import {
  parseIntakeDecisions,
  validateIntakeDecisions,
} from '@cogo/agentic-generator/multiblock/intake-decisions'
import { createDefaultAppSpecInteractionContract } from '@cogo/agentic-generator/multiblock/interaction-contract'
import { getStudioAccessToken } from '@/services/auth-provider'
import { useStudioStore } from '@/store/studio-store'
import type {
  ConfirmedBlock,
  ConfirmedPage,
  ThemeHint,
} from '@/store/conversation-types'
import type { DesignSystemState } from '@/features/design-system'
import { encodeAppSpecDesignSystemTokens } from '@/features/design-system/lib/theme-tokens'
import { designSystemGenerationAuthorityFields } from '@/features/design-system/lib/project-design-system-generation-binding'
import {
  buildStudioDesignSystemContract,
  slugDesignSystemId,
} from '@/features/design-system/lib/design-system-export'
import type { StudioRuntimeTargetEvidence } from '@/features/validation/studio-generation-evidence'
import {
  deriveStudioProjectPlanFromGeneration,
  runtimeTargetsForPrompt,
  type StudioDecomposePlanningMetadata,
} from '@/features/project/lib/studio-project-plan'
import { withApplicationResponsibilityDecisions } from './application-responsibility'
import { withStudioOntologyRetrievalTrace } from './ontology-retrieval-evidence'
import { withActionFlowRuntimeSimulationProof } from './actionflow-runtime-simulation'
import { isReportApplicationPrompt, withStudioReportApplicationIntegration } from './report-application-integration'
import { buildTerminologySelectionModel } from './terminology-selection-model'
import { currentTerminologyScope } from './terminology-scope-selection'
import { evaluatePromptPlanFidelity } from './prompt-plan-fidelity'
import {
  livePlannerFailureMessage,
  livePlannerMappedMessage,
  livePlannerPlannedMessage,
  livePlannerProgressMessage,
  livePlannerReturnedInMessage,
  livePlannerStageMessage,
} from './live-planner-copy'
import {
  COGO_DEFAULT_PRIMARY_COLOR,
  createDefaultStudioThemeHint,
} from './studio-theme-policy'
import {
  APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
  APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE,
  StudioApplicationOntologyGenerationRefusal,
  parseApplicationOntologyGenerationPlanningObservation,
  parseApplicationOntologyGenerationPlanningObservationForBootstrap,
  type ApplicationOntologyGenerationBootstrapInput,
  type ApplicationOntologyGenerationPlanningObservation,
  type StudioApplicationOntologyGenerationContext,
} from '@/features/application-ontology/application-ontology-generation'

export interface PromptAppPlan {
  appSpec: AppSpecFull
  pages: ConfirmedPage[]
  blocks: ConfirmedBlock[]
  themeHint?: ThemeHint
  designSystemTokens?: Record<string, string>
  designSystem?: Record<string, string>
  designSystemContract?: Record<string, unknown>
  planningMetadata?: StudioDecomposePlanningMetadata
  /** End-user SKILL.md for operating the generated app (from cogo-decompose-api). */
  skill?: string
  /**
   * Generic Application Ontology Program — Studio Task 2. Present only for a
   * package-bound generation, and only when the server actually produced it:
   * the exact echoed binding plus the model-invocation and worker refs. A
   * bound plan is never returned without it.
   */
  applicationOntologyGeneration?: ApplicationOntologyGenerationPlanningObservation
}

interface PromptAppOptions {
  designSystemPreset?: DesignSystemState | null
  /** Library lane: semantic planning only; Design Ontology owns presentation. */
  designOntologyPlanningMode?: 'semantic-only'
  signal?: AbortSignal
  timeoutMs?: number
  onPlanningProgress?: (progress: DecomposePlanningProgress) => void
  /**
   * The FIVE-field public bootstrap, and the one thing that makes this request
   * package-bound. Serialized beside the ordinary prompt in the existing
   * `/api/decompose` request; there is no second endpoint and no second
   * request shape.
   */
  applicationOntologyBootstrap?: ApplicationOntologyGenerationBootstrapInput
  /**
   * SERVER-COMPOSED ONLY. `functions/api/decompose.ts` produces this after a
   * canonical admitted verification and passes it on the internal path; a
   * browser caller that sets it is rejected outright rather than having it
   * quietly ignored (an ignored authority field is how a forged one becomes
   * indistinguishable from an absent one).
   */
  applicationOntologyGeneration?: StudioApplicationOntologyGenerationContext
  /** Server-prepared Chat subject; Pages replays and verifies it before planning. */
  chatGeneratedApplicationDraft?: import('./chat-generated-application-draft-api').ChatGeneratedApplicationDraftPreparationReceipt
  /** Server-prepared Bot subject plus the exact approved request used for replay verification. */
  botGeneratedApplicationDraft?: import('../../bot-mode/bot-generated-application-draft-api').BotGeneratedApplicationDraftPlanningBinding
}

export type DecomposePlanningPhase =
  | 'auth'
  | 'request'
  | 'response'
  | 'mapping'
  | 'fallback'
  | 'timeout'
  | 'done'

export interface DecomposePlanningProgress {
  phase: DecomposePlanningPhase
  message: string
  elapsedMs?: number
  upstreamMs?: number
  totalMs?: number
}

// Production Qwen may need two bounded repair passes after exact requirement
// validation. Keep the client alive for all attempts; a shorter single-call
// deadline aborts a correct repair before the server can return it.
export const STUDIO_DECOMPOSE_HARD_TIMEOUT_MS = 150_000
const DECOMPOSE_REQUIREMENT_REVIEW_PROGRESS_MS = 45_000
const DECOMPOSE_VALIDATION_PROGRESS_MS = 100_000

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === 'AbortError'
    : err instanceof Error && err.name === 'AbortError'
}

function parseTimingHeader(value: string | null): number | undefined {
  if (!value) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function parseAttemptCountHeader(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined
  const parsed = Number(value)
  return parsed >= 1 && parsed <= 3 ? parsed : undefined
}

function parseBooleanHeader(value: string | null): boolean | undefined {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function buildDefaultInteractionContract(): NonNullable<
  AppSpecFull['interactionContract']
> {
  return createDefaultAppSpecInteractionContract()
}
function actionFlowBinds(
  kind:
    | 'counter-profile'
    | 'preference-profile'
    | 'order-flow'
    | 'task-flow'
    | 'generic-workflow'
    | 'dashboard'
    | 'mobile-workflow'
    | 'backoffice-table',
): string[] {
  switch (kind) {
    case 'counter-profile':
      return [
        'increment button tap -> incrementCounter ActionFlow -> local.count + _results.incrementResult',
        'name input change -> updateName ActionFlow -> local.name',
        'submit button tap -> submitProfile ActionFlow -> appData.profile.name + _results.submitResult + computed.greeting',
        'task input change -> updateTaskTitle ActionFlow -> local.taskTitle',
        'add task tap -> addTask ActionFlow -> local DB append appData.tasks + _results.addTaskResult',
        'complete first task tap -> completeFirstTask ActionFlow -> local DB updateWhere appData.tasks + _results.completeTaskResult',
        'sync score tap -> syncProfileScore ActionFlow -> external datasource _results.profileScore + appData.profile.score',
      ]
    case 'preference-profile':
      return [
        'email input change -> updateEmail ActionFlow -> local.email',
        'newsletter toggle tap -> toggleNewsletter ActionFlow -> local.newsletter + _results.togglePreferenceResult',
        'save preferences tap -> savePreferences ActionFlow -> appData.preferences + _results.savePreferenceResult + computed.preferenceSummary',
      ]
    case 'order-flow':
      return [
        'menu item tap -> selectItem ActionFlow -> local.selectedItem',
        'quantity change -> updateQuantity ActionFlow -> local.quantity',
        'add to cart tap -> addToCart ActionFlow -> appData.cart + _results.addToCartResult + computed.cartSummary',
      ]
    case 'task-flow':
      return [
        'task input change -> updateDraftTask ActionFlow -> local.draftTask',
        'add task tap -> addTask ActionFlow -> appData.tasks + _results.addTaskResult + computed.taskSummary',
        'task checkbox change -> toggleTask ActionFlow -> appData.tasks',
      ]
    case 'generic-workflow':
      return [
        'details input change -> updateDetails ActionFlow -> local.details',
        'review tap -> prepareReview ActionFlow -> local.prepared + _results.reviewResult',
        'confirm tap -> completeWorkflow ActionFlow -> appData.workflow + computed.workflowStatus',
      ]
    case 'dashboard':
      return [
        'filter change -> applyFilter ActionFlow -> local.activeFilter',
        'refresh tap -> refreshMetrics ActionFlow -> appData.metrics + _results.refreshMetricsResult + computed.metricSummary',
      ]
    case 'mobile-workflow':
      return [
        'bottom navigation tap -> switchMobileTab ActionFlow -> local.activeTab',
        'search input change -> updateMobileQuery ActionFlow -> local.query',
        'primary CTA tap -> completeMobileTask ActionFlow -> appData.mobileTask + _results.mobileTaskResult + computed.mobileStatus',
        'offline toggle tap -> toggleOfflineMode ActionFlow -> local.offlineMode + _results.offlineModeResult',
      ]
    case 'backoffice-table':
      return [
        'data-table rowClick -> selectRecord ActionFlow -> local.selectedRecord + _results.selectRecordResult',
        'data-table cellChange -> editRecordCell ActionFlow -> local.records via setTableCell + _results.editCellResult',
        'mark first row tap -> markFirstRecordFulfilled ActionFlow -> setTableCell local.records[0].status + computed.tableSummary',
        'add row tap -> addRecordRow ActionFlow -> addTableRow local.records + _results.addRecordResult',
        'delete row tap -> deleteFirstRecord ActionFlow -> deleteTableRow local.records + _results.deleteRecordResult',
      ]
  }
}

function buildInteractionContract(
  overrides: Partial<NonNullable<AppSpecFull['interactionContract']>> = {},
): NonNullable<AppSpecFull['interactionContract']> {
  const base = buildDefaultInteractionContract()
  return {
    ...base,
    ...overrides,
    requiredEventTypes: overrides.requiredEventTypes ?? base.requiredEventTypes,
    requiredStateScopes:
      overrides.requiredStateScopes ?? base.requiredStateScopes,
    notes: [...base.notes, ...(overrides.notes ?? [])],
  }
}

function buildStaticInteractionContract(): NonNullable<
  AppSpecFull['interactionContract']
> {
  return {
    actionFlowRequired: false,
    requiredEventTypes: [],
    requiredStateScopes: [],
    requireResultsTrace: false,
    requireComputedProjection: false,
    notes: [
      'Static web-only generation must not add datasources, external adapters, live-service calls, or native build targets.',
      'CTA buttons may render as non-functional links/buttons until a user explicitly requests forms, checkout, booking, or integrations.',
    ],
  }
}

function titleFromPrompt(prompt: string): string {
  const explicitName = promptQuotedName(prompt)
  if (explicitName) return explicitName

  const cleaned = prompt
    .replace(/^(create|build|make|generate)\s+(an?\s+)?/i, '')
    .replace(/\s+(app|application|website|site)$/i, '')
    .trim()

  const base = cleaned || prompt.trim() || 'Custom App'
  return base
    .split(/\s+/)
    .slice(0, 8)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function slugify(input: string): string {
  const slug = input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  return slug || 'custom-app'
}

function cleanPromptAnchor(value: string | null | undefined): string | null {
  if (!value) return null
  const cleaned = value
    .replace(/\bprod-chat-[a-z0-9-]+(?:-\d{3}z)?\b/gi, ' ')
    .replace(/\bpreview-chat-[a-z0-9-]+(?:-\d{3}z)?\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s"'“”]+|[\s"'“”.,;:!?)]+$/g, '')
    .trim()
  return cleaned.length >= 3 ? cleaned : null
}

function promptQuotedName(prompt: string): string | null {
  const match = prompt.match(/\bnamed\s+["“]([^"”]+)["”]/i)
  return cleanPromptAnchor(match?.[1])
}

function promptForPhrase(prompt: string): string | null {
  const match = prompt.match(
    /\bfor\s+(?:a|an|the)?\s*([^.;]+?)(?=\b(?:include|use|make|build|open|with)\b|[.;]|$)/i,
  )
  return cleanPromptAnchor(match?.[1])
}

function promptBuildDomainPhrase(prompt: string): string | null {
  const matches = prompt.matchAll(
    /\b(?:build|make|create)\s+(?:a|an|the)?\s*([^.;]+?)(?=\s+(?:with|using|that|and)\b|[.;]|$)/gi,
  )
  for (const match of matches) {
    const cleaned = cleanPromptAnchor(match[1])
    if (!cleaned) continue
    if (/\b(?:studio\s+)?app(?:lication)?\b/i.test(cleaned)) continue
    return cleanPromptAnchor(
      cleaned.replace(
        /^(?:lightweight|simple|quick|interactive|shared|new|independent)\s+/i,
        '',
      ),
    )
  }
  return null
}

function promptDomainAnchors(prompt: string, appName: string): string[] {
  const candidates = [
    appName,
    promptQuotedName(prompt),
    promptForPhrase(prompt),
    promptBuildDomainPhrase(prompt),
    /\bfield\s+task\s+tracker\b/i.test(prompt) ? 'field task tracker' : null,
    /\btask\s+tracker\b/i.test(prompt) ? 'task tracker' : null,
    /\bcogo\s+platform\b/i.test(prompt) ? 'Coogo platform' : null,
    /\bfine\s+dining\s+restaurant\b/i.test(prompt)
      ? 'fine dining restaurant'
      : null,
    /\brestaurant\s+landing\s+page\b/i.test(prompt)
      ? 'restaurant landing page'
      : null,
    /\bproductivity\s+notebook\b/i.test(prompt)
      ? 'productivity notebook'
      : null,
  ]

  const seen = new Set<string>()
  return candidates
    .flatMap((candidate) => {
      const cleaned = cleanPromptAnchor(candidate)
      if (!cleaned) return []
      const key = cleaned.toLowerCase()
      if (seen.has(key)) return []
      seen.add(key)
      return [cleaned]
    })
    .slice(0, 3)
}

const PROMPT_INTERACTION_TERMS =
  /\b(?:action|actionable|button|click|tap|submit|reset|restart|return|open|close|edit|delete|approve|reject|next|back|filter|search|select|send|save|add|remove|update|promote)\w*\b/i

const PROMPT_INTERACTION_STOP_WORDS = new Set([
  'block',
  'component',
  'display',
  'form',
  'list',
  'panel',
  'section',
])

function promptInteractionClauses(prompt: string): string[] {
  return prompt
    .split(/[.!?]+/)
    .map((clause) => clause.trim())
    .filter(
      (clause) =>
        clause.length >= 12 && PROMPT_INTERACTION_TERMS.test(clause),
    )
}

function semanticTokens(value: string): Set<string> {
  return new Set(
    value
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(
        (token) =>
          token.length >= 3 && !PROMPT_INTERACTION_STOP_WORDS.has(token),
      ),
  )
}

function withPromptInteractionRequirements(
  blocks: ConfirmedBlock[],
  prompt: string,
): ConfirmedBlock[] {
  const clauses = promptInteractionClauses(prompt)
  if (clauses.length === 0) return blocks

  return blocks.map((block) => {
    const identityTokens = semanticTokens(`${block.name} ${block.type}`)
    if (identityTokens.size === 0) return block

    const matchedClauses = clauses.filter((clause) => {
      const clauseTokens = semanticTokens(clause)
      const overlap = [...identityTokens].filter((token) =>
        clauseTokens.has(token),
      )
      return overlap.length >= Math.min(2, identityTokens.size)
    })
    if (matchedClauses.length === 0) return block

    const uniqueClauses = matchedClauses.filter(
      (clause, index) =>
        matchedClauses.indexOf(clause) === index &&
        !block.description.toLowerCase().includes(clause.toLowerCase()),
    )
    if (uniqueClauses.length === 0) return block

    return {
      ...block,
      description: [
        block.description.trim(),
        'Prompt interaction requirements:',
        ...uniqueClauses.map((clause) => `- ${clause}`),
      ].join('\n'),
    }
  })
}

function withPromptFidelitySpecs(
  blocks: ConfirmedBlock[],
  prompt: string,
  appName: string,
): ConfirmedBlock[] {
  const anchors = promptDomainAnchors(prompt, appName)
  if (anchors.length === 0) return blocks

  const primaryIndex = Math.max(
    0,
    blocks.findIndex((block) =>
      /hero|header|overview|summary/i.test(`${block.name} ${block.type}`),
    ),
  )
  return blocks.map((block, index) => {
    if (index !== primaryIndex) return block

    const requiredElements = anchors.map((text, anchorIndex) => ({
      role: anchorIndex === 0 ? 'prompt-app-title' : 'prompt-domain-anchor',
      context:
        'Required prompt-fidelity text; prevents generation from drifting into unrelated template corpus content.',
      matchBy: {
        text: [text],
        id: [`${block.name}-prompt-anchor-${anchorIndex + 1}`],
      },
      requiredText: text,
    }))

    return {
      ...block,
      specV3: {
        blockType: block.type,
        matchNames: [block.name, block.type],
        ui: {
          ...(block.specV3?.ui ?? {}),
          requiredElements: [
            ...(block.specV3?.ui.requiredElements ?? []),
            ...requiredElements,
          ],
        },
        flow: block.specV3?.flow ?? { required: {} },
        events: block.specV3?.events ?? { required: {} },
        ...(block.specV3?.state ? { state: block.specV3.state } : {}),
        ...(block.specV3?.data ? { data: block.specV3.data } : {}),
      },
    }
  })
}

function stripNegatedPromptClauses(input: string): string {
  return input
    .replace(/\b(?:no|without)\s+[^,.;]+/g, ' ')
    .replace(
      /\bdo not\s+(?:add|build|create|generate|include|make|target|use)\s+[^,.;]+/g,
      ' ',
    )
    .replace(
      /\bdon't\s+(?:add|build|create|generate|include|make|target|use)\s+[^,.;]+/g,
      ' ',
    )
}

function hasExplicitDynamicCapabilityIntent(positiveText: string): boolean {
  const reservationCtaOnly =
    /\breservation\s+(?:call\s+to\s+action|cta)\b/.test(positiveText)
  const operationalReservation =
    /\b(?:booking|appointment|calendar|availability|schedule|scheduling)\b/.test(
      positiveText,
    ) ||
    (/\breservation\b/.test(positiveText) &&
      !reservationCtaOnly &&
      /\b(?:system|form|flow|engine|manage|confirm|slot|slots)\b/.test(
        positiveText,
      ))

  return (
    operationalReservation ||
    /\b(?:ordering|checkout|cart|payment|payments|buy|purchase|e-?commerce|storefront|product catalog|selectable inventory)\b/.test(
      positiveText,
    ) ||
    /\b(?:dashboard|admin|analytics|crm|report|metric|backoffice|back office|data\s*table|editable table|bulk edit)\b/.test(
      positiveText,
    ) ||
    /\b(?:form|login|auth|database|datasource|data source|live service|external adapter|workflow|todo|task)\b/.test(
      positiveText,
    ) ||
    /\b(?:mobile app|native app|ios|android|react native|flutter)\b/.test(
      positiveText,
    )
  )
}

function isStaticWebOnlyPrompt(lower: string, positiveText: string): boolean {
  const asksForWebOnly =
    /\b(web[-\s]?only|target web only|web target only)\b/.test(lower)
  const asksForStatic =
    /\b(static|one[-\s]?page|landing page|marketing page|brochure|portfolio)\b/.test(
      positiveText,
    )
  const blocksDynamicCapabilities =
    /\b(?:no|without)\s+[^,.;]*(?:live services?|datasources?|data sources?|external adapters?|forms?|login|auth|checkout|booking|availability|payments?|native|mobile)\b/.test(
      lower,
    ) ||
    /\bdo not\s+(?:add|build|create|generate|include|make|target|use)\s+[^,.;]*(?:live services?|datasources?|data sources?|external adapters?|forms?|login|auth|checkout|booking|availability|payments?|native|mobile)\b/.test(
      lower,
    )

  return (
    (asksForWebOnly || asksForStatic) &&
    (blocksDynamicCapabilities ||
      asksForWebOnly ||
      !hasExplicitDynamicCapabilityIntent(positiveText))
  )
}

function inferRuntimeTargets(prompt: string): StudioRuntimeTargetEvidence[] {
  return runtimeTargetsForPrompt(prompt) as StudioRuntimeTargetEvidence[]
}

interface InferredPromptPlan {
  pages: ConfirmedPage[]
  blocks: ConfirmedBlock[]
  appClass?: AppSpecFull['appClass']
  datasources?: AppSpecFull['datasources']
  globalState?: AppSpecFull['globalState']
  interactionContract?: AppSpecFull['interactionContract']
  runtimeTargets?: StudioRuntimeTargetEvidence[]
  goldenRecipeIds?: NonNullable<AppSpecFull['goldenRecipePack']>['recipeIds']
}

function inferPlan(prompt: string): InferredPromptPlan {
  const lower = prompt.toLowerCase()
  const positiveText = stripNegatedPromptClauses(lower)

  if (isStaticWebOnlyPrompt(lower, positiveText)) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'hero',
        type: 'hero',
        description: `Static web-only hero for ${prompt}. Use text, semantic tokens, and local visual primitives only; do not use external images or live data.`,
        binds: [],
      },
      {
        name: 'feature-grid',
        type: 'grid',
        description:
          'Static feature grid with concise cards and no datasource, product catalog, or runtime fetch.',
        binds: [],
      },
      {
        name: 'pricing',
        type: 'section',
        description:
          'Static pricing or offer section with non-functional CTA buttons only.',
        binds: [],
      },
      {
        name: 'faq',
        type: 'section',
        description:
          'Static FAQ section with visible answers and no accordion state requirement.',
        binds: [],
      },
      {
        name: 'final-cta',
        type: 'section',
        description:
          'Static final call-to-action section; do not add forms, checkout, booking, authentication, or adapters.',
        binds: [],
      },
    ]
    return {
      pages: [
        {
          id: 'home',
          title: 'Home',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'static',
      runtimeTargets: ['web'],
      datasources: [],
      globalState: {},
      interactionContract: buildStaticInteractionContract(),
    }
  }

  if (/(deterministic.*counter.*profile|counter.*profile)/.test(positiveText)) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'counter-profile',
        type: 'deterministic-counter-profile',
        description:
          'Counter profile block with input, buttons, local state, appData writes, _results trace, and computed greeting',
        binds: actionFlowBinds('counter-profile'),
      },
    ]
    return {
      pages: [
        {
          id: 'home',
          title: 'Home',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
    }
  }

  if (/(toggle.*preference.*profile|preference.*profile)/.test(positiveText)) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'preference-profile',
        type: 'toggle-preference-profile',
        description:
          'Preference profile block with email input, newsletter toggle, appData writes, _results trace, and computed summary',
        binds: actionFlowBinds('preference-profile'),
      },
    ]
    return {
      pages: [
        {
          id: 'home',
          title: 'Home',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
    }
  }

  if (
    /(complex\s+mobile|mobile\s+app|ios|android|field\s+ops|field\s+service|native\s+app|consumer\s+mobile)/.test(
      positiveText,
    )
  ) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'mobile-shell',
        type: 'mobile-shell',
        description:
          'Mobile app shell with bottom navigation, compact cards, and safe-area aware layout',
        binds: [
          'bottom navigation items are rendered as tap targets and reflected in local.activeTab',
        ],
      },
      {
        name: 'mobile-workflow',
        type: 'form',
        description:
          'Complex mobile workflow with search, offline toggle, primary CTA, local/appData writes, and computed status',
        binds: actionFlowBinds('mobile-workflow'),
      },
      {
        name: 'mobile-detail',
        type: 'detail-panel',
        description:
          'Mobile detail and confirmation panel that reflects the latest ActionFlow state',
        binds: [
          'computed mobile status is visible after primary CTA execution',
        ],
      },
    ]
    return {
      pages: [
        {
          id: 'mobile',
          title: 'Mobile',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
      runtimeTargets: ['mobile'],
      globalState: {
        appData: {
          type: 'object',
          default: {
            mobileTask: null,
          },
        },
      },
      interactionContract: buildInteractionContract({
        requiredEventTypes: ['change', 'tap'],
        requiredStateScopes: ['local', 'appData'],
        notes: [
          'Complex mobile generation must include bottom navigation, form/input interaction, local state, appData writes, and computed renderer projection.',
          'Mobile proof must run in a mobile viewport; desktop-only render evidence is not sufficient.',
        ],
      }),
      goldenRecipeIds: ['complex-mobile'],
    }
  }

  if (
    /(backoffice|back\s+office|data\s*table|editable\s+table|table\s+(edit|operation|manipulation)|bulk\s+edit|operations\s+console|order\s+management|inventory\s+ops)/.test(
      positiveText,
    )
  ) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'operations-summary',
        type: 'dashboard',
        description:
          'Backoffice KPI summary for operational status, exceptions, and queue health',
        binds: [],
      },
      {
        name: 'records-table',
        type: 'data-table',
        description:
          'Editable backoffice data table with row selection, cell editing, setTableCell, addTableRow, and deleteTableRow ActionFlow coverage',
        binds: actionFlowBinds('backoffice-table'),
      },
      {
        name: 'bulk-actions',
        type: 'form',
        description:
          'Backoffice bulk action toolbar connected to the table selection and mutation flows',
        binds: [
          'bulk action tap -> table mutation ActionFlow -> local.records + _results.bulkActionResult',
        ],
      },
    ]
    return {
      pages: [
        {
          id: 'backoffice',
          title: 'Backoffice',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
      datasources: [
        {
          name: 'orders',
          type: 'localTable',
          schema: {
            id: 'string',
            customer: 'string',
            status: 'string',
            total: 'number',
          },
        },
      ],
      globalState: {
        appData: {
          type: 'object',
          default: {
            selectedRecord: null,
          },
        },
      },
      interactionContract: buildInteractionContract({
        requiredEventTypes: ['cellChange', 'rowClick', 'tap'],
        requiredStateScopes: ['local', 'appData'],
        notes: [
          'Backoffice generation must use a data-table renderer for editable rows, not a static list.',
          'Backoffice proof must show rowClick, cellChange, setTableCell, addTableRow, and deleteTableRow ActionFlow evidence.',
        ],
      }),
      goldenRecipeIds: ['backoffice-data-table'],
    }
  }

  if (isReportApplicationPrompt(positiveText)) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'global-command-header',
        type: 'command-header',
        description:
          'Global command header with WebMCP chat command input, source upload affordance, report period controls, and export status.',
        binds: [
          'chat command submit -> loadReport ActionFlow -> appData.reportSource + appData.reportRows + _results.loadReportResult',
          'period filter change -> selectTable ActionFlow -> local.activeFilter + appData.filteredRows + computed.reportSummary',
        ],
      },
      {
        name: 'report-navigation',
        type: 'sidebar-navigation',
        description: 'Collapsible full-height left navigation for report sections and source-backed evidence groups.',
        binds: [
          'sidebar section tap -> selectSection ActionFlow -> local.selectedSection + _results.selectSectionResult',
        ],
      },
      {
        name: 'a4-report-canvas',
        type: 'document-page',
        description:
          'A4 portrait report canvas with source-backed narrative, mixed bar/line chart, and data table using semantic @ds tokens.',
        binds: [
          'report canvas init -> loadReport ActionFlow -> appData.previewChartRows + appData.previewTableRows + computed.reportNarrative',
        ],
      },
      {
        name: 'report-chart-table-composition',
        type: 'chart-table-composition',
        description:
          'Report body composition with narrative, chart surface, source table, and shared local CSV/json manifest data.',
        binds: [
          'chart series toggle tap -> selectChart ActionFlow -> local.activeChart + computed.reportNarrative + _results.selectChartResult',
        ],
      },
      {
        name: 'document-export-panel',
        type: 'document-export-panel',
        description:
          'Document export panel for PDF, DOCX/HWPX, XLSX, and CSV readiness; Excel workbook formulas are extraction-only.',
        binds: ['export document tap -> exportDocument ActionFlow -> appData.exportArtifacts + _results.exportResult'],
      },
    ]
    return {
      pages: [
        {
          id: 'report',
          title: 'Report',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
      datasources: [
        {
          name: 'localCsvManifest',
          type: 'local-file',
          schema: {
            fileName: 'string',
            sheetName: 'string',
            rowCount: 'number',
            checksum: 'string',
          },
        },
        {
          name: 'reportChartSurfaces',
          type: 'json-manifest',
          schema: {
            chartId: 'string',
            chartKind: 'string',
            sourceRange: 'string',
            rendered: 'boolean',
          },
        },
        {
          name: 'documentExportArtifacts',
          type: 'local-artifact',
          schema: {
            format: 'string',
            artifactRef: 'string',
            readiness: 'string',
            generatedAt: 'string',
          },
        },
      ],
      globalState: {
        appData: {
          type: 'object',
          default: {
            reportSource: null,
            reportRows: [],
            filteredRows: [],
            previewChartRows: [],
            previewTableRows: [],
            exportArtifacts: [],
          },
        },
      },
      interactionContract: buildInteractionContract({
        requiredEventTypes: ['change', 'tap', 'submit', 'fileSelect'],
        requiredStateScopes: ['local', 'appData', '_results', 'computed'],
        notes: [
          'Report app proof must show local file/CSV/json-manifest source ingestion, ActionFlow state updates, A4 report preview, and document export readiness.',
          'The product shell must stay separate from validation harness artifacts.',
        ],
      }),
    }
  }

  if (/(dashboard|admin|analytics|crm|report|metric)/.test(positiveText)) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'overview-metrics',
        type: 'dashboard',
        description: `Key metrics and status for ${prompt}`,
        binds: [],
      },
      {
        name: 'activity-feed',
        type: 'list',
        description: 'Recent activity and prioritized work items',
        binds: [],
      },
      {
        name: 'insight-panel',
        type: 'section',
        description:
          'Actionable insights, filters, and next-step recommendations',
        binds: actionFlowBinds('dashboard'),
      },
    ]
    return {
      pages: [
        {
          id: 'dashboard',
          title: 'Dashboard',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
    }
  }

  if (
    /\b(cafe|restaurant|menu|order|ordering|commerce|shop|store|product|products|checkout)\b/.test(
      positiveText,
    )
  ) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'hero',
        type: 'hero',
        description: `Brand and ordering entry point for ${prompt}`,
        binds: [],
      },
      {
        name: 'catalog',
        type: 'grid',
        description: `Product-first catalog for ${prompt} with selectable inventory and visible media`,
        binds: [],
      },
      {
        name: 'order-flow',
        type: 'form',
        description: 'Primary ordering, checkout, or request flow',
        binds: actionFlowBinds('order-flow'),
      },
    ]
    return {
      pages: [
        {
          id: 'home',
          title: 'Home',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
      interactionContract: buildInteractionContract({
        notes: [
          'Shopping proof must show catalog selection, quantity change, add-to-cart ActionFlow, appData.cart, _results, and computed cart summary projection.',
        ],
      }),
      goldenRecipeIds: ['shopping'],
    }
  }

  if (
    /(booking|schedule|reservation|appointment|calendar)/.test(positiveText)
  ) {
    const blocks: ConfirmedBlock[] = [
      {
        name: 'booking-hero',
        type: 'hero',
        description: `Booking entry point for ${prompt}`,
        binds: [],
      },
      {
        name: 'availability',
        type: 'calendar',
        description: 'Availability, slots, and date selection',
        binds: [],
      },
      {
        name: 'confirmation',
        type: 'form',
        description: 'Reservation details and confirmation actions',
        binds: [],
      },
    ]
    return {
      pages: [
        {
          id: 'booking',
          title: 'Booking',
          route: '/',
          blocks: blocks.map((b) => b.name),
        },
      ],
      blocks,
    }
  }

  if (/\b(todo|task|checklist|kanban)\b/.test(positiveText)) {
    const blocks: ConfirmedBlock[] = [
    {
      name: 'task-overview',
        type: 'dashboard',
        description:
          'Task progress, ownership, due-date, and priority summary derived from the current task collection',
        binds: [],
      },
      {
        name: 'task-capture',
        type: 'form',
        description: 'Create a task with title, owner, priority, and due date, then review the saved result',
        binds: actionFlowBinds('task-flow'),
      },
      {
        name: 'task-board',
        type: 'list',
        description:
          'Filterable task board with meaningful project work, visible completion state, and no product-conformance placeholder content',
        binds: ['task row change -> toggleTask ActionFlow -> appData.tasks + computed.taskSummary'],
      },
    ]
    return {
      pages: [
        {
          id: 'tasks',
          title: 'Tasks',
          route: '/',
          blocks: blocks.map((block) => block.name),
        },
      ],
      blocks,
      appClass: 'dynamic',
      interactionContract: buildInteractionContract({
        notes: [
          'Task application proof must create and complete user-authored work; generated-code review and conformance placeholders are forbidden.',
        ],
      }),
    }
  }

  const blocks: ConfirmedBlock[] = [
    {
      name: 'hero',
      type: 'hero',
      description: `Primary introduction for ${prompt}`,
      binds: [],
    },
    {
      name: 'feature-grid',
      type: 'grid',
      description: 'Core features, sections, or content modules',
      binds: [],
    },
    {
      name: 'primary-workflow',
      type: 'form',
      description: 'Main user workflow with clear call to action',
      binds: actionFlowBinds('generic-workflow'),
    },
  ]
  return {
    pages: [
      {
        id: 'home',
        title: 'Home',
        route: '/',
        blocks: blocks.map((b) => b.name),
      },
    ],
    blocks,
    appClass: 'dynamic',
  }
}

function deriveTheme(
  designSystemPreset: DesignSystemState | null | undefined,
): {
  themeHint: ThemeHint
  designSystemTokens?: Record<string, string>
  designSystem?: Record<string, string>
  designSystemContract?: Record<string, unknown>
} {
  if (!designSystemPreset) {
    return {
      themeHint: createDefaultStudioThemeHint(),
    }
  }

  const tokens = encodeAppSpecDesignSystemTokens(designSystemPreset)
  const authority = designSystemGenerationAuthorityFields({
    themeHint: {
      primaryColor: tokens.primary ?? COGO_DEFAULT_PRIMARY_COLOR,
      style: designSystemPreset.style,
      fontFamily: designSystemPreset.font,
    },
    designSystemTokens: tokens,
  })
  return {
    ...authority,
    designSystemContract: buildStudioDesignSystemContract(
      designSystemPreset,
    ) as unknown as Record<string, unknown>,
  }
}

function withAdaptivePlanningMetadata(
  appSpec: AppSpecFull,
  prompt: string,
  designSystemPreset: DesignSystemState | null | undefined,
  semanticOnly = false,
): AppSpecFull {
  const designSystemId = designSystemPreset ? slugDesignSystemId(designSystemPreset) : undefined
  const projectPlan = deriveStudioProjectPlanFromGeneration({
    prompt,
    name: appSpec.name,
    appClass: appSpec.appClass,
    description: appSpec.description,
    runtimeTargets: appSpec.runtimeTargets,
    targetPlatform: appSpec.targetPlatform,
    generationTargetPlatform: appSpec.generationTargetPlatform,
    interactionContract: appSpec.interactionContract,
    datasources: appSpec.datasources,
    globalState: appSpec.globalState,
    designSystemId,
  })
  const semanticProjectPlan = semanticOnly ? { ...projectPlan } : projectPlan
  if (semanticOnly) {
    delete semanticProjectPlan.designSystemId
    delete semanticProjectPlan.stylePresetId
    delete semanticProjectPlan.templateId
  }

  return {
    ...appSpec,
    projectPlan: semanticProjectPlan,
    adaptiveRuntime: semanticProjectPlan.adaptiveRuntime,
  }
}

function inferGoldenRecipeIds(
  prompt: string,
  inferred: InferredPromptPlan,
): NonNullable<AppSpecFull['goldenRecipePack']>['recipeIds'] {
  if (inferred.appClass === 'static') return []
  const lower = stripNegatedPromptClauses(prompt.toLowerCase())
  const ids = new Set<
    NonNullable<AppSpecFull['goldenRecipePack']>['recipeIds'][number]
  >(inferred.goldenRecipeIds ?? [])
  if (
    /(cart|checkout|commerce|shopping|shop|store|\bproduct\b|catalog|menu|ordering)/.test(
      lower,
    )
  )
    ids.add('shopping')
  if (
    /(complex\s+mobile|mobile\s+app|ios|android|field\s+ops|field\s+service|native\s+app|consumer\s+mobile)/.test(
      lower,
    )
  )
    ids.add('complex-mobile')
  if (
    /(backoffice|back\s+office|data\s*table|editable\s+table|table\s+(edit|operation|manipulation)|bulk\s+edit|operations\s+console|order\s+management|inventory\s+ops)/.test(
      lower,
    )
  )
    ids.add('backoffice-data-table')
  return [...ids]
}

function buildGoldenRecipePack(
  recipeIds: NonNullable<AppSpecFull['goldenRecipePack']>['recipeIds'],
): AppSpecFull['goldenRecipePack'] | undefined {
  if (recipeIds.length === 0) return undefined
  const recipes: NonNullable<AppSpecFull['goldenRecipePack']>['recipes'] =
    recipeIds.map((scenarioId) => {
      switch (scenarioId) {
        case 'shopping':
          return {
            id: 'recipe.shopping.checkout.v1',
            scenarioId,
            label: 'Shopping checkout application',
            requiredBlocks: ['catalog', 'order-flow'],
            designQualityGates: [
              'Use an 8px spacing scale; adjacent order controls must not touch.',
              'Primary CTA and quantity controls must meet 44px minimum touch target size.',
              'Use semantic design tokens for surfaces, text, focus, and status colors.',
            ],
            actionFlowContracts: [
              'catalog selection updates local.selectedItem',
              'quantity change updates local.quantity',
              'add-to-cart tap writes appData.cart and _results.addToCartResult',
            ],
            stateContracts: [
              'local state remains block-scoped',
              'cart summary projects through computed state',
            ],
            datasourceContracts: [
              'catalog data may be local fixture data until a real datasource is attached',
            ],
            browserProof: [
              'select item',
              'change quantity',
              'tap add to cart',
              'observe cart summary state projection',
            ],
          }
        case 'complex-mobile':
          return {
            id: 'recipe.mobile.workflow.v1',
            scenarioId,
            label: 'Complex mobile workflow application',
            requiredBlocks: [
              'mobile-shell',
              'mobile-workflow',
              'mobile-detail',
            ],
            designQualityGates: [
              'Mobile layout must use consistent gap/padding and safe-area-aware controls.',
              'Bottom navigation and primary CTA must meet 44px touch target size.',
              'Dense mobile cards must preserve readable hierarchy without overlapping text.',
            ],
            actionFlowContracts: [
              'bottom navigation tap updates local.activeTab',
              'search input change updates local.query',
              'primary CTA writes appData.mobileTask and _results.mobileTaskResult',
            ],
            stateContracts: [
              'mobile blocks keep local state scoped per block',
              'shared task state writes through appData.mobileTask',
            ],
            datasourceContracts: [
              'offline mode must degrade without pretending datasource proof exists',
            ],
            browserProof: [
              'run mobile viewport',
              'switch tab',
              'search',
              'toggle offline mode',
              'complete primary CTA',
            ],
          }
        case 'backoffice-data-table':
          return {
            id: 'recipe.backoffice.table.v1',
            scenarioId,
            label: 'Backoffice editable data table application',
            requiredBlocks: [
              'operations-summary',
              'records-table',
              'bulk-actions',
            ],
            designQualityGates: [
              'Toolbar controls must wrap or grid responsively with visible gaps.',
              'Data table must sit inside card/header/content anatomy and keep horizontal overflow contained.',
              'Pagination and row action controls must not collapse into adjacent buttons.',
            ],
            actionFlowContracts: [
              'rowClick selects local.selectedRecord',
              'cellChange executes setTableCell and records _results.editCellResult',
              'add/delete row actions mutate local.records with trace evidence',
            ],
            stateContracts: [
              'table edit state remains local until saved',
              'selected record may be mirrored to appData.selectedRecord',
            ],
            datasourceContracts: [
              'orders datasource schema includes id, customer, status, and total',
            ],
            browserProof: [
              'select row',
              'edit cell',
              'mark first row fulfilled',
              'add row',
              'delete row',
            ],
          }
      }
    })

  return {
    schemaVersion: 'studio-golden-recipe-pack.v1',
    recipeIds,
    designSystemGates: {
      spacingScalePx: 8,
      minTouchTargetPx: 44,
      requireSemanticTokens: true,
      forbidAdjacentControlsWithoutGap: true,
      requireMeasuredDesignEvidence: true,
    },
    recipes,
  }
}

export function buildAppSpecFromPrompt(
  prompt: string,
  options: PromptAppOptions = {},
): PromptAppPlan {
  const name = titleFromPrompt(prompt)
  const inferred = inferPlan(prompt)
  const { pages } = inferred
  const blocks = withPromptFidelitySpecs(
    withPromptInteractionRequirements(inferred.blocks, prompt),
    prompt,
    name,
  )
  const semanticOnly =
    options.designOntologyPlanningMode === 'semantic-only'
  const { themeHint, designSystemTokens, designSystem, designSystemContract } = semanticOnly
    ? {}
    : deriveTheme(options.designSystemPreset)
  const runtimeTargets = inferred.runtimeTargets ?? inferRuntimeTargets(prompt)
  const goldenRecipePack = semanticOnly
    ? undefined
    : buildGoldenRecipePack(inferGoldenRecipeIds(prompt, inferred))
  const plannedAppSpec = withStudioReportApplicationIntegration(
    withAdaptivePlanningMetadata(
      {
        appId: `${slugify(name)}-new3`,
        name,
        description: prompt.trim(),
        appClass: inferred.appClass ?? 'dynamic',
        runtimeTargets,
        pages,
        blocks,
        datasources: inferred.datasources ?? [],
        globalState: inferred.globalState ?? {
          appData: { type: 'object', default: {} },
        },
        interactionContract:
          inferred.interactionContract ?? buildDefaultInteractionContract(),
        ...(themeHint ? { themeHint } : {}),
        ...(goldenRecipePack ? { goldenRecipePack } : {}),
        ...(designSystemTokens ? { designSystemTokens } : {}),
        ...(designSystem ? { designSystem } : {}),
        ...(designSystemContract ? { designSystemContract } : {}),
      },
      prompt,
      semanticOnly ? undefined : options.designSystemPreset,
      semanticOnly,
    ),
    { prompt, source: 'local-prompt-planner' },
  )
  const appSpecWithOntology = withStudioOntologyRetrievalTrace(
    withApplicationResponsibilityDecisions(
      plannedAppSpec,
      // `prompt` here too, not only on the outer call. The INNER call is the
      // one that writes `ontologyRetrievalTrace` on a spec that has none, and
      // the outer then keeps the plan it finds
      // (`normalizeStudioOntologyRetrievalTrace`: `remoteTrace
      // .actionFlowOntologyPlan ?? fallback.actionFlowOntologyPlan`). Passing
      // the submission only to the outer call computes the right plan and
      // discards it.
      { prompt, source: 'local-prompt-planner' },
    ),
    // `prompt`: what the person typed. Without it the ActionFlow plan is
    // derived from the description this planner just wrote.
    { prompt, source: 'local-prompt-planner' },
  )
  const localAppSpec = withActionFlowRuntimeSimulationProof(
    appSpecWithOntology,
    {
      source: 'local-prompt-planner',
    },
  )
  // The fallback planner carries the intake decisions too: with no decompose
  // server reachable, the structured record must still land on the appSpec
  // (and from there in project settings) rather than surviving only as text.
  const intakeDecisions = parseIntakeDecisions(prompt)
  const appSpec: AppSpecFull = intakeDecisions
    ? { ...localAppSpec, intakeDecisions }
    : localAppSpec

  return {
    appSpec,
    pages: appSpec.pages ?? pages,
    blocks: appSpec.blocks.map((block) => ({
      ...block,
      binds: block.binds ?? [],
    })),
    ...(themeHint ? { themeHint } : {}),
    ...(designSystemTokens ? { designSystemTokens } : {}),
    ...(designSystem ? { designSystem } : {}),
    ...(designSystemContract ? { designSystemContract } : {}),
  }
}

// ── Real-LLM planner: the cogo-decompose-api Cloudflare Worker ───────────────
// When `VITE_COGO_DECOMPOSE_URL` is configured, the chat plans the app with the
// real-LLM multiblock decomposer (replacing the regex `inferPlan`). The worker
// returns a derived `AppSpecFull` whose block descriptions carry the shared-state
// contract, so the existing `/api/v2/app/generate` pipeline composes the blocks.
// Returns `null` when the API cannot provide an admitted live plan. The product
// generation boundary treats that as fail-closed and creates no project; the
// local prompt planner remains available only to isolated design/test callers.

/**
 * The subset of the decompose API's `appSpec` we consume (mirror of AppSpecFull).
 *
 * EXPORTED for one reason: the offline E2E harness stands in for the decompose
 * proxy (vite-plugins/e2e-mock-decompose-planner.ts), and a harness whose
 * response shape is a hand-written parallel copy of this interface can drift
 * from it silently — which is the whole way a mock starts lying. The mock
 * derives its own types from these, so renaming a field here is a COMPILE
 * ERROR in the mock rather than a runtime surprise nobody sees until the
 * next live run.
 */
export interface DecomposeApiAppSpec {
  name?: string
  description?: string
  /**
   * Studio Task 2 — the staged planning observation the decompose worker
   * produced for a bound request. `unknown` on purpose: it is validated by
   * the parsers in `@/features/application-ontology`, never trusted by shape.
   */
  applicationOntologyGeneration?: unknown
  goldenSkuId?: AppSpecFull['goldenSkuId']
  appClass?: AppSpecFull['appClass']
  runtimeTargets?: StudioRuntimeTargetEvidence[]
  pages?: Array<{ id: string; route: string; title: string; blocks: string[] }>
  blocks?: Array<{
    name: string
    type: string
    description: string
    binds?: string[]
  }>
  globalState?: Record<string, { type: string; default: unknown }>
  interactionContract?: AppSpecFull['interactionContract']
  generationResponsibilities?: AppSpecFull['generationResponsibilities']
  ontologyRetrievalTrace?: AppSpecFull['ontologyRetrievalTrace']
  actionFlowTopology?: AppSpecFull['actionFlowTopology']
  actionFlowValidationProof?: AppSpecFull['actionFlowValidationProof']
  actionFlowBindingPlan?: AppSpecFull['actionFlowBindingPlan']
  actionFlowCompiledFlows?: AppSpecFull['actionFlowCompiledFlows']
  actionFlowCompilerProof?: AppSpecFull['actionFlowCompilerProof']
  actionFlowRuntimeSimulationProof?: AppSpecFull['actionFlowRuntimeSimulationProof']
  actionFlowReadiness?: AppSpecFull['actionFlowReadiness']
  applicationKind?: AppSpecFull['applicationKind']
  targetAudience?: AppSpecFull['targetAudience']
  isValidationHarness?: AppSpecFull['isValidationHarness']
  reportApplicationSchemaVersion?: AppSpecFull['reportApplicationSchemaVersion']
  reportApplicationKind?: AppSpecFull['reportApplicationKind']
  datasources?: AppSpecFull['datasources']
  /** Server echo of the consumed intake decisions — validated before trust. */
  intakeDecisions?: unknown
}

/** See DecomposeApiAppSpec above for why these two are exported. */
export interface DecomposeApiResponseBody {
  appSpec?: DecomposeApiAppSpec
  planning?: unknown
  planner?: unknown
  metadata?: unknown
  meta?: unknown
  /** End-user SKILL.md the worker ships alongside the app spec. */
  skill?: string
}

type DecomposeRequestTarget =
  | { kind: 'proxy'; url: string }
  | { kind: 'direct'; url: string; token?: string }
  | { kind: 'blocked'; reason: string }

function envFlag(value: unknown): boolean {
  return value === true || value === 'true'
}

function isProductionBuild(): boolean {
  return envFlag(import.meta.env.PROD) || import.meta.env.MODE === 'production'
}

function readProxyContract(
  value: string | null | undefined,
): StudioDecomposePlanningMetadata['proxyContract'] | undefined {
  return value === 'appSpec' ||
    value === 'missing-appSpec' ||
    value === 'non-json'
    ? value
    : undefined
}

function resolveDecomposeRequestTarget(): DecomposeRequestTarget | null {
  const proxyUrl = (
    import.meta.env.VITE_COGO_DECOMPOSE_PROXY_URL as string | undefined
  )?.trim()
  if (proxyUrl) return { kind: 'proxy', url: proxyUrl }

  const baseUrl = (
    import.meta.env.VITE_COGO_DECOMPOSE_URL as string | undefined
  )?.trim()
  if (!baseUrl) return null
  if (isProductionBuild()) {
    return {
      kind: 'blocked',
      reason:
        'Live planner direct URL is disabled in production; configure the Studio proxy route.',
    }
  }

  const token = (
    import.meta.env.VITE_COGO_DECOMPOSE_TOKEN as string | undefined
  )?.trim()
  return {
    kind: 'direct',
    url: `${baseUrl.replace(/\/+$/, '')}/v1/decompose`,
    ...(token ? { token } : {}),
  }
}

async function getStudioProxyAuthHeaders(): Promise<Record<string, string>> {
  const accessToken = await getStudioAccessToken()
  if (!accessToken) return {}

  const anonKey = (
    import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
  )?.trim()
  return {
    Authorization: `Bearer ${accessToken}`,
    ...(anonKey ? { apikey: anonKey } : {}),
  }
}

export async function buildAppPlanFromDecomposeApi(
  prompt: string,
  options: PromptAppOptions = {},
): Promise<PromptAppPlan | null> {
  // Generic Application Ontology Program — Studio Task 2.
  //
  // `bound` is decided by the presence of the generic bootstrap, never by the
  // profile's identity: the same prompt, with and without it, takes the two
  // different lanes below.
  const bootstrap = options.applicationOntologyBootstrap
  const bound = Boolean(bootstrap)
  if (options.applicationOntologyGeneration !== undefined) {
    // The private post-admission context is server-composed. A browser caller
    // that supplies one is refused, not tolerated.
    throw new StudioApplicationOntologyGenerationRefusal(
      APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
    )
  }
  /**
   * For a BOUND request there is no fallback outcome at all: the caller gets
   * the operational refusal instead of a `null` that a surface would read as
   * "plan unavailable, carry on". No verifier receipt, no PASS and no evidence
   * `BLOCKED` is created on this path — a refusal here is an outage or a
   * forbidden shortcut, never a judgement about the application.
   */
  const failClosedIfBound = (
    code:
      | typeof APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE
      | typeof APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
  ): void => {
    if (bound) throw new StudioApplicationOntologyGenerationRefusal(code)
  }

  const target = resolveDecomposeRequestTarget()
  if (!target) {
    options.onPlanningProgress?.({
      phase: 'fallback',
      message: livePlannerFailureMessage('unavailable'),
    })
    failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE)
    return null
  }
  if (target.kind === 'blocked') {
    console.warn('[decompose-api] live planner configuration blocked', {
      reason: target.reason,
    })
    options.onPlanningProgress?.({
      phase: 'fallback',
      message: livePlannerFailureMessage('configuration'),
    })
    failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE)
    return null
  }
  const lower = prompt.toLowerCase()
  if (isStaticWebOnlyPrompt(lower, stripNegatedPromptClauses(lower))) {
    options.onPlanningProgress?.({
      phase: 'fallback',
      message: livePlannerFailureMessage('static-unsupported'),
    })
    failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN)
    return null
  }

  // Structured intake decisions (WonBanking S1-S4/S7 lane): the ONE shared
  // grammar reads the cards' decision blocks out of the prompt here, so the
  // server receives data it can validate instead of prose it would have to
  // re-scrape. Null for ordinary prompts — nothing changes for them.
  const clientIntakeDecisions = parseIntakeDecisions(prompt)

  let apiAppSpec: DecomposeApiAppSpec
  let planningMetadata: StudioDecomposePlanningMetadata
  let skillMd: string | undefined
  let planningObservation:
    | ApplicationOntologyGenerationPlanningObservation
    | undefined
  const startedAt = performance.now()
  const emitProgress = (progress: DecomposePlanningProgress) =>
    options.onPlanningProgress?.(progress)
  let timedOut = false
  let timeout: ReturnType<typeof setTimeout> | null = null
  const progressTimeouts: Array<ReturnType<typeof setTimeout>> = []
  const controller = new AbortController()
  const abortFromCaller = () =>
    controller.abort(
      new DOMException('Decompose request aborted', 'AbortError'),
    )
  try {
    if (options.signal?.aborted)
      throw new DOMException('Decompose request aborted', 'AbortError')
    emitProgress({ phase: 'auth', message: livePlannerStageMessage('checking-session') })
    const authHeaders =
      target.kind === 'proxy'
        ? await getStudioProxyAuthHeaders()
        : target.token
          ? { Authorization: `Bearer ${target.token}` }
          : {}

    if (options.signal?.aborted)
      throw new DOMException('Decompose request aborted', 'AbortError')
    options.signal?.addEventListener('abort', abortFromCaller, { once: true })
    timeout = setTimeout(() => {
      timedOut = true
      controller.abort(
        new DOMException('Decompose request timed out', 'AbortError'),
      )
    }, options.timeoutMs ?? STUDIO_DECOMPOSE_HARD_TIMEOUT_MS)

    emitProgress({
      phase: 'request',
      message: livePlannerProgressMessage('planning'),
    })
    progressTimeouts.push(
      setTimeout(() => {
        if (controller.signal.aborted) return
        emitProgress({
          phase: 'request',
          message: livePlannerProgressMessage('requirements'),
          elapsedMs: DECOMPOSE_REQUIREMENT_REVIEW_PROGRESS_MS,
        })
      }, DECOMPOSE_REQUIREMENT_REVIEW_PROGRESS_MS),
      setTimeout(() => {
        if (controller.signal.aborted) return
        emitProgress({
          phase: 'request',
          message: livePlannerProgressMessage('validating'),
          elapsedMs: DECOMPOSE_VALIDATION_PROGRESS_MS,
        })
      }, DECOMPOSE_VALIDATION_PROGRESS_MS),
    )
    const terminologyScope = currentTerminologyScope()
    const res = await fetch(target.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      // `terminologyScope` is `undefined` when no project is open or the
      // open project declares none — JSON.stringify drops undefined-valued
      // keys, so the body stays byte-identical to `{ prompt }` in that case.
      // `intakeDecisions` follows the same rule: the intake cards' decision
      // blocks are parsed out of the prompt by the shared grammar and travel
      // STRUCTURED alongside it (the raw text still rides in `prompt`, so an
      // older server keeps today's behaviour; a current server strips the
      // blocks and consumes the data).
      // `applicationOntologyBootstrap` follows the same undefined-drop rule:
      // an unbound request's body stays byte-identical to what it was before
      // Task 2 existed. Only the FIVE public fields travel — no attempt, no
      // KG binding, no receipt, no eligibility, no admission, no context.
      body: JSON.stringify({
        prompt,
        terminologyScope,
        intakeDecisions: clientIntakeDecisions ?? undefined,
        applicationOntologyBootstrap: bootstrap ?? undefined,
        chatGeneratedApplicationDraft:
          options.chatGeneratedApplicationDraft ?? undefined,
        botGeneratedApplicationDraft:
          options.botGeneratedApplicationDraft ?? undefined,
      }),
      signal: controller.signal,
    })
    const elapsedMs = Math.round(performance.now() - startedAt)
    const responseHeaders = res.headers as Headers | undefined
    const authMs = parseTimingHeader(
      responseHeaders?.get('x-cogo-decompose-auth-ms') ?? null,
    )
    const upstreamMs = parseTimingHeader(
      responseHeaders?.get('x-cogo-decompose-upstream-ms') ?? null,
    )
    const totalMs = parseTimingHeader(
      responseHeaders?.get('x-cogo-decompose-total-ms') ?? null,
    )
    emitProgress({
      phase: 'response',
      message: livePlannerReturnedInMessage(Math.round(elapsedMs / 1000)),
      elapsedMs,
      upstreamMs,
      totalMs,
    })
    if (!res.ok) {
      console.warn(
        `[decompose-api] HTTP ${res.status}; refusing local promotion`,
      )
      // The proxy classifies the upstream failure into a closed set and
      // reports it as `x-cogo-decompose-failure-reason` (functions/api/
      // decompose-failure-reason.ts). Without reading it, every 422 collapsed
      // into `invalid-response` — which is why today's Korean
      // requirement-coverage blocker showed the creator "could not be
      // verified safely" instead of the authored, actionable copy. Unknown or
      // absent values fall back to the previous status-only mapping, so no
      // new string can reach the creator through this path.
      const reportedReason = responseHeaders?.get(
        'x-cogo-decompose-failure-reason',
      )
      const failureReason =
        reportedReason === 'requirement-coverage'
          ? 'scope-mismatch'
          : reportedReason === 'not-configured'
            ? 'configuration'
            : reportedReason === 'timeout'
              ? 'timeout'
              : res.status === 422
                ? 'invalid-response'
                : res.status === 504
                  ? 'timeout'
                  : 'unavailable'
      emitProgress({
        phase: 'fallback',
        message: livePlannerFailureMessage(failureReason),
        elapsedMs,
      })
      failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE)
      return null
    }
    const body = (await res.json()) as DecomposeApiResponseBody | null
    const spec = body?.appSpec
    skillMd = typeof body?.skill === 'string' ? body.skill : undefined
    if (
      !spec?.name ||
      !Array.isArray(spec.blocks) ||
      spec.blocks.length === 0
    ) {
      console.warn(
        '[decompose-api] malformed appSpec; refusing local promotion',
      )
      emitProgress({
        phase: 'fallback',
        message: livePlannerFailureMessage('invalid-response'),
        elapsedMs,
      })
      failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN)
      return null
    }
    const promptPlanFidelity = evaluatePromptPlanFidelity({
      prompt,
      blocks: spec.blocks,
      globalState: spec.globalState,
    })
    if (!promptPlanFidelity.admitted) {
      console.warn('[decompose-api] prompt-plan fidelity rejected', {
        missingDomains: promptPlanFidelity.missingDomains,
        conflictingDomains: promptPlanFidelity.conflictingDomains,
      })
      emitProgress({
        phase: 'fallback',
        message: livePlannerFailureMessage('scope-mismatch'),
        elapsedMs,
      })
      failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN)
      return null
    }
    // A bound plan is admitted only if the server actually produced the
    // staged observation: the EXACT binding echoed back, plus the two refs
    // that cannot exist before the model ran. Anything else is a plan
    // produced outside the binding.
    if (bootstrap) {
      const observed = options.applicationOntologyGeneration
        ? parseApplicationOntologyGenerationPlanningObservation(
            spec.applicationOntologyGeneration,
            (options.applicationOntologyGeneration as StudioApplicationOntologyGenerationContext)
              .binding,
          )
        : parseApplicationOntologyGenerationPlanningObservationForBootstrap(
            spec.applicationOntologyGeneration,
            bootstrap,
          )
      if (!observed) {
        throw new StudioApplicationOntologyGenerationRefusal(
          APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN,
        )
      }
      planningObservation =
        observed as unknown as ApplicationOntologyGenerationPlanningObservation
    }
    const planningRecord =
      readRecord(body?.planning) ??
      readRecord(body?.planner) ??
      readRecord(body?.metadata) ??
      readRecord(body?.meta)
    const model =
      readString(responseHeaders?.get('x-cogo-decompose-model')) ??
      readString(planningRecord?.model)
    const provider =
      readString(responseHeaders?.get('x-cogo-decompose-provider')) ??
      readString(planningRecord?.provider)
    const proxyContract = readProxyContract(
      responseHeaders?.get('x-cogo-decompose-proxy-contract'),
    )
    const decomposeWorkerVersion =
      readString(responseHeaders?.get('x-cogo-decompose-worker-version')) ??
      readString(planningRecord?.decomposeWorkerVersion) ??
      readString(planningRecord?.workerVersion)
    const attemptCount =
      parseAttemptCountHeader(
        responseHeaders?.get('x-cogo-decompose-attempt-count') ?? null,
      ) ??
      (typeof planningRecord?.attemptCount === 'number' &&
      planningRecord.attemptCount >= 1 &&
      planningRecord.attemptCount <= 3
        ? planningRecord.attemptCount
        : undefined)
    const repairAttempted =
      parseBooleanHeader(
        responseHeaders?.get('x-cogo-decompose-repair-attempted') ?? null,
      ) ??
      (typeof planningRecord?.repairAttempted === 'boolean'
        ? planningRecord.repairAttempted
        : undefined)
    planningMetadata = {
      schemaVersion: 'studio-decompose-planning.v1',
      planningSource: 'cogo-decompose-api',
      status: 'live',
      plannerMode: target.kind,
      requestUrl: target.url,
      generatedAt: new Date().toISOString(),
      ...(authMs !== undefined ? { authMs } : {}),
      ...(upstreamMs !== undefined ? { upstreamMs } : {}),
      ...(totalMs !== undefined ? { totalMs } : {}),
      elapsedMs,
      ...(model ? { model } : {}),
      ...(provider ? { provider } : {}),
      ...(decomposeWorkerVersion ? { decomposeWorkerVersion } : {}),
      ...(attemptCount !== undefined ? { attemptCount } : {}),
      ...(repairAttempted !== undefined ? { repairAttempted } : {}),
      ...(proxyContract ? { proxyContract } : {}),
    }
    emitProgress({
      phase: 'mapping',
      message: livePlannerStageMessage('mapping-plan'),
      elapsedMs,
    })
    apiAppSpec = spec
  } catch (err) {
    // A bound refusal raised above is the RESULT, not an incident to be
    // converted back into the `null` fallback this very block exists to
    // produce for the unbound lane.
    if (err instanceof StudioApplicationOntologyGenerationRefusal) throw err
    const elapsedMs = Math.round(performance.now() - startedAt)
    if (timedOut) {
      emitProgress({
        phase: 'timeout',
        message: livePlannerFailureMessage('timeout'),
        elapsedMs,
      })
      console.warn('[decompose-api] request timed out; no project promoted')
      failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE)
      return null
    }
    if (options.signal?.aborted || isAbortError(err)) {
      throw err
    }
    console.warn('[decompose-api] request failed; no project promoted', err)
    emitProgress({
      phase: 'fallback',
      message: livePlannerFailureMessage('unavailable'),
      elapsedMs,
    })
    failClosedIfBound(APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE)
    return null
  } finally {
    if (timeout) clearTimeout(timeout)
    for (const progressTimeout of progressTimeouts) {
      clearTimeout(progressTimeout)
    }
    options.signal?.removeEventListener('abort', abortFromCaller)
  }

  const name = apiAppSpec.name?.trim() || titleFromPrompt(prompt)
  const semanticOnly =
    options.designOntologyPlanningMode === 'semantic-only'
  const { themeHint, designSystemTokens, designSystem, designSystemContract } = semanticOnly
    ? {}
    : deriveTheme(options.designSystemPreset)
  const goldenRecipePack = semanticOnly
    ? undefined
    : buildGoldenRecipePack(
        inferGoldenRecipeIds(prompt, {
          pages: [],
          blocks: [],
          appClass: apiAppSpec.appClass,
          runtimeTargets: apiAppSpec.runtimeTargets,
        }),
      )

  const blocks: ConfirmedBlock[] = withPromptFidelitySpecs(
    withPromptInteractionRequirements(
      apiAppSpec.blocks!.map((b) => ({
        name: b.name,
        type: b.type,
        description: b.description,
        binds: Array.isArray(b.binds) ? b.binds : [],
      })),
      prompt,
    ),
    prompt,
    name,
  )
  const pages: ConfirmedPage[] =
    apiAppSpec.pages && apiAppSpec.pages.length
      ? apiAppSpec.pages.map((p) => ({
          id: p.id,
          title: p.title,
          route: p.route,
          blocks: p.blocks,
        }))
      : [
          {
            id: 'home',
            title: 'Home',
            route: '/',
            blocks: blocks.map((b) => b.name),
          },
        ]

  emitProgress({
    phase: 'mapping',
    message: livePlannerMappedMessage(pages.length, blocks.length),
    elapsedMs: Math.round(performance.now() - startedAt),
  })
  const plannedAppSpec = withStudioReportApplicationIntegration(
    withAdaptivePlanningMetadata(
      {
        appId: `${slugify(name)}-new3`,
        name,
        description: apiAppSpec.description?.trim() || prompt.trim(),
        ...(apiAppSpec.goldenSkuId
          ? { goldenSkuId: apiAppSpec.goldenSkuId }
          : {}),
        ...(apiAppSpec.appClass ? { appClass: apiAppSpec.appClass } : {}),
        ...(apiAppSpec.applicationKind ? { applicationKind: apiAppSpec.applicationKind } : {}),
        ...(apiAppSpec.targetAudience ? { targetAudience: apiAppSpec.targetAudience } : {}),
        ...(apiAppSpec.isValidationHarness !== undefined ? { isValidationHarness: apiAppSpec.isValidationHarness } : {}),
        ...(apiAppSpec.reportApplicationSchemaVersion
          ? { reportApplicationSchemaVersion: apiAppSpec.reportApplicationSchemaVersion }
          : {}),
        ...(apiAppSpec.reportApplicationKind ? { reportApplicationKind: apiAppSpec.reportApplicationKind } : {}),
        ...(apiAppSpec.runtimeTargets
          ? { runtimeTargets: apiAppSpec.runtimeTargets }
          : {}),
        pages,
        blocks,
        datasources: apiAppSpec.datasources ?? [],
        // Per-symbol globalState from the decomposition (richer than the regex planner's
        // single `appData` object; the worker's raw-key binds resolve against these).
        globalState: apiAppSpec.globalState ?? {
          appData: { type: 'object', default: {} },
        },
        interactionContract:
          apiAppSpec.interactionContract ?? buildDefaultInteractionContract(),
        ...(apiAppSpec.generationResponsibilities
          ? { generationResponsibilities: apiAppSpec.generationResponsibilities }
          : {}),
        ...(apiAppSpec.ontologyRetrievalTrace
          ? { ontologyRetrievalTrace: apiAppSpec.ontologyRetrievalTrace }
          : {}),
        ...(apiAppSpec.actionFlowTopology
          ? { actionFlowTopology: apiAppSpec.actionFlowTopology }
          : {}),
        ...(apiAppSpec.actionFlowValidationProof
          ? { actionFlowValidationProof: apiAppSpec.actionFlowValidationProof }
          : {}),
        ...(apiAppSpec.actionFlowBindingPlan
          ? { actionFlowBindingPlan: apiAppSpec.actionFlowBindingPlan }
          : {}),
        ...(apiAppSpec.actionFlowCompiledFlows
          ? { actionFlowCompiledFlows: apiAppSpec.actionFlowCompiledFlows }
          : {}),
        ...(apiAppSpec.actionFlowCompilerProof
          ? { actionFlowCompilerProof: apiAppSpec.actionFlowCompilerProof }
          : {}),
        ...(apiAppSpec.actionFlowRuntimeSimulationProof
          ? {
              actionFlowRuntimeSimulationProof:
                apiAppSpec.actionFlowRuntimeSimulationProof,
            }
          : {}),
        ...(apiAppSpec.actionFlowReadiness
          ? { actionFlowReadiness: apiAppSpec.actionFlowReadiness }
          : {}),
        ...(themeHint ? { themeHint } : {}),
        ...(goldenRecipePack ? { goldenRecipePack } : {}),
        ...(designSystemTokens ? { designSystemTokens } : {}),
        ...(designSystem ? { designSystem } : {}),
        ...(designSystemContract ? { designSystemContract } : {}),
        ...(planningMetadata
          ? {
              planningSource: planningMetadata.planningSource,
              decomposePlanning: planningMetadata,
            }
          : {}),
      },
      prompt,
      semanticOnly ? undefined : options.designSystemPreset,
      semanticOnly,
    ),
    { prompt, source: 'decompose-api' },
  )
  emitProgress({
    phase: 'mapping',
    message: livePlannerStageMessage('validating-ontology'),
    elapsedMs: Math.round(performance.now() - startedAt),
  })
  const appSpecWithOntology = withStudioOntologyRetrievalTrace(
    withApplicationResponsibilityDecisions(
      plannedAppSpec,
      // Same seam as the local planner above: the inner call writes the trace,
      // the outer keeps the plan it finds. On this path the two differ for
      // real — `apiAppSpec.description` is the decompose API's rewrite, so
      // without this the archetype is chosen from generated text.
      { prompt, source: 'decompose-api' },
    ),
    // `prompt`: the submission, not `apiAppSpec.description` — which is what
    // the decompose API wrote back FROM the submission.
    { prompt, source: 'decompose-api' },
  )
  emitProgress({
    phase: 'mapping',
    message: livePlannerStageMessage('simulating-actionflow'),
    elapsedMs: Math.round(performance.now() - startedAt),
  })
  const appSpecWithProof = withActionFlowRuntimeSimulationProof(
    appSpecWithOntology,
    {
      source: 'decompose-api',
    },
  )
  // Prefer the server's echo — it states what the planner was actually
  // constrained by — validated through the same grammar before trust; fall
  // back to the client parse so the decisions persist even against an older
  // server that ignored the field.
  const intakeDecisions =
    validateIntakeDecisions(apiAppSpec.intakeDecisions) ?? clientIntakeDecisions
  const appSpec: AppSpecFull = intakeDecisions
    ? { ...appSpecWithProof, intakeDecisions }
    : appSpecWithProof

  emitProgress({
    phase: 'done',
    message: livePlannerPlannedMessage(pages.length, blocks.length),
    elapsedMs: Math.round(performance.now() - startedAt),
  })

  // Terminology selection UI plan (Task 3,
  // docs/superpowers/plans/terminology-selection-ui-plan.md) — EXCEPTION-path
  // card appended only on a genuinely successful decompose (every early
  // `return null` above already happened). Read from the RAW `apiAppSpec`,
  // not `appSpec` above: normalizeStudioOntologyRetrievalTrace
  // (ontology-retrieval-evidence.ts) rebuilds ontologyRetrievalTrace from a
  // fixed field whitelist and does not carry terminologyDiagnosis forward —
  // by design, this is a one-time decision at arrival, not persisted app-spec
  // evidence. Fail-open: a parsing/model-building error here must never
  // reject this otherwise-successful decompose, and no card is shown unless
  // a project is currently open (nothing to declare a scope on otherwise).
  try {
    const diagnosis = apiAppSpec.ontologyRetrievalTrace?.terminologyDiagnosis
    const selectionModel = diagnosis
      ? buildTerminologySelectionModel(diagnosis)
      : null
    if (selectionModel && useStudioStore.getState().projectMeta) {
      useStudioStore.getState().appendChatMessage({
        kind: 'terminology-selection',
        id: `terminology-selection-${crypto.randomUUID()}`,
        timestamp: Date.now(),
        model: selectionModel,
      })
    }
  } catch (err) {
    console.warn('[decompose-api] terminology-selection model build failed', err)
  }

  return {
    appSpec,
    pages: appSpec.pages ?? pages,
    blocks: appSpec.blocks.map((block) => ({
      ...block,
      binds: block.binds ?? [],
    })),
    ...(themeHint ? { themeHint } : {}),
    ...(planningMetadata ? { planningMetadata } : {}),
    ...(designSystemTokens ? { designSystemTokens } : {}),
    ...(designSystem ? { designSystem } : {}),
    ...(designSystemContract ? { designSystemContract } : {}),
    ...(skillMd ? { skill: skillMd } : {}),
    // A bound plan carries the staged observation it was admitted with. It is
    // never absent here: the `bootstrap` branch above throws before this
    // return when the server did not produce one.
    ...(planningObservation
      ? { applicationOntologyGeneration: planningObservation }
      : {}),
  }
}
